import { Col, Row } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import ConditionalRender from "../../../../reusable-components/ConditionalRender";
import OrderDetailsSummary from "./OrderSummaryView";
import InputField from "../../../../reusable-components/TNInputField";
import { InputfieldTypes } from "../../../../enums/InputFieldAttributesEnums";
import {
  AddTicketFormConstants,
  getCustomersOptions,
  getMaterialOptions,
  getTruckNumbersOptions,
  getVendorOptions,
  getJobOptions,
  SELECT_PLACEHOLDER,
  getOrdersOptions,
  addTicketConstantStatus,
  OPTION_FILTER_PROP,
} from "./constants";
import AddTicketDragger from "../AddTicketDragger";
import TicketUploadServices from "../../../../services/ticketUpload.service";
import { getOrderDetailSummary } from "../../../TicketUpload/TicketUploadDrawer/AddTicketForm/constants";
import { FileTypes } from "../../../../enums/FileUploadEnums";
import moment from "moment";
import { DateFormat } from "../../../../enums/dateTimeFormat";
import styles from "../TicketUploadDrawer.module.scss";
import { storageKeys } from "../../../../constants/storageKeys";

const AddTicketForm = ({ setUploadTicketPayload, isOpen }) => {
  //User Id Details
  const userDetails = localStorage.getItem(storageKeys.USER_DETAILS_KEY);
  const userDetailsJson = userDetails ? JSON.parse(userDetails) : null;
  const userRoleInfo = userDetailsJson?.userRoleInfo;
  const userId = userDetailsJson?.id;

  const { watch, setValue, getValues } = useFormContext();

  const {
    FetchTicketUploadTrucks,
    FetchTicketUploadCustomers,
    FetchTicketUploadJobs,
    FetchTicketOrderNumbersMeta,
    FetchTicketUploadMaterial,
    FetchTicketUploadVendor
  } = TicketUploadServices();
  //Selected Values
  const customerValue = watch(AddTicketFormConstants.CUSTOMER_SELECT_NAME);
  const jobValue = watch(AddTicketFormConstants.JOB_SELECT_NAME);
  const orderValue = watch(AddTicketFormConstants.ORDER_SELECT_NAME);
  const materialValue = watch(AddTicketFormConstants.MATERIAL_SELECT_NAME);
  const vendorValue = watch(AddTicketFormConstants.QUARRY_SELECT_NAME);
  const truckValue = watch(AddTicketFormConstants.TRUCK_SELECT_NAME);

  const ticketUploadParams = {
    materialId: materialValue,
    vendorId: vendorValue,
    companyId: customerValue,
    orderId: orderValue,
    jobId: jobValue,
  };

  const { data: trucksData, isFetching: trucksFetching } =
    FetchTicketUploadTrucks();

  const {
    data: customersData,
    refetch: refetchCustomersData,
    isFetching: customersFetching,
  } = FetchTicketUploadCustomers(ticketUploadParams);

  const {
    data: jobsData,
    refetch: refetchJobsData,
    isFetching: jobsFetching,
  } = FetchTicketUploadJobs(ticketUploadParams);

  const {
    data: ordersData,
    refetch: refetchOrdersData,
    isFetching: ordersFetching,
  } = FetchTicketOrderNumbersMeta(ticketUploadParams);

  const {
    data: materialData,
    refetch: refetchMaterialData,
    isFetching: materialFetching,
  } = FetchTicketUploadMaterial(ticketUploadParams);

  const {
    data: vendorData,
    refetch: refetchVendorData,
    isFetching: vendorFetching,
  } = FetchTicketUploadVendor(ticketUploadParams);

  const [formSelectionStack, setFormSelectionStack] = useState([]);

  const handleFieldChange = (key, value) => {
    const stackIndex = formSelectionStack.indexOf(key);

    if (stackIndex === -1) {
      setFormSelectionStack((prev) => [...prev, key]);
    } else {
      const sliceIndex = value === null ? stackIndex : stackIndex + 1;
      const newStack = formSelectionStack.slice(0, sliceIndex);

      const keysToRemove = formSelectionStack.slice(sliceIndex);

      keysToRemove.forEach((keyToRemove) => {
        setValue(keyToRemove, undefined);
      });

      setFormSelectionStack(newStack);
    }
  };
  const truckNumbersOptions = getTruckNumbersOptions(trucksData?.data);
  const materialOptions = getMaterialOptions(
    materialData?.data,
  );
  const vendorOptions = getVendorOptions(vendorData?.data);
  const customersOptions = getCustomersOptions(customersData?.data);
  const jobOptions = getJobOptions(jobsData?.data);
  const orderOptions = useMemo(
    () => getOrdersOptions(ordersData?.data),
    [ordersData],
  );

  const [orderDetails, setOrderDetails] = useState(null);
  const [documentKey, setDocumentKey] = useState(undefined);

  useEffect(() => {
    if (!customerValue) {
      refetchCustomersData();
    }
    if (!jobValue) {
      refetchJobsData();
    }
    if (!orderValue) {
      refetchOrdersData();
    }
    if (!materialValue) {
      refetchMaterialData();
    }
    if (!vendorValue) {
      refetchVendorData();
    }
  }, [customerValue, jobValue, orderValue, materialValue, vendorValue]);

  useEffect(() => {
    setOrderDetails(
      ordersData?.data?.find((order) => order?.order?.id === orderValue),
    );
  }, [orderValue]);

  useEffect(() => {
    // Effect to remove selected order if not in listed options
    const selectedOrder = orderOptions?.find(
      (item) => item.value === orderValue,
    );
    if (!selectedOrder && !!orderValue)
      setValue(AddTicketFormConstants.ORDER_SELECT_NAME, undefined);
  }, [orderOptions]);

  useEffect(() => {
    if (orderDetails && truckValue) {
      setUploadTicketPayload({
        ...orderDetails,
        orderId: orderDetails?.order?.id,
        createdBy: userId,
        truckNumber: truckValue,
        truckInfo: {
          ...trucksData?.data?.find((truck) => truck?.truckId === truckValue),
        },
        ticketUploadPicture: documentKey,
        uploadStatus: addTicketConstantStatus.uploadReviewStatus,
        ticketVerification: addTicketConstantStatus.ticketVerificationStatus,
        uploadDate: moment().format(DateFormat.MM_DD_YYYY_SLASHED),
      });
    }
  }, [orderDetails, truckValue, documentKey]);
  return (
    <>
      <div className={styles.uploadInputWrapper}>
        <Row>
          <Col span={12}>
            <InputField
              type={InputfieldTypes.SELECT}
              name={AddTicketFormConstants.CUSTOMER_SELECT_NAME}
              label={AddTicketFormConstants.CUSTOMER_SELECT_LABEL}
              placeholder={SELECT_PLACEHOLDER}
              selectLoading={customersFetching}
              requiredField
              options={customersOptions}
              allowClear
              showSearch
              optionFilterProp={OPTION_FILTER_PROP}
              changeHandler={(value) => {
                handleFieldChange(
                  AddTicketFormConstants.CUSTOMER_SELECT_NAME,
                  value,
                );
              }}
            />
          </Col>
          <Col span={12}>
            <InputField
              type={InputfieldTypes.SELECT}
              name={AddTicketFormConstants.JOB_SELECT_NAME}
              label={AddTicketFormConstants.JOB_SELECT_LABEL}
              placeholder={SELECT_PLACEHOLDER}
              requiredField
              options={jobOptions}
              allowClear
              showSearch
              selectLoading={jobsFetching}
              optionFilterProp={OPTION_FILTER_PROP}
              changeHandler={(value) => {
                handleFieldChange(
                  AddTicketFormConstants.JOB_SELECT_NAME,
                  value,
                );
              }}
            />
          </Col>
          <Col span={12}>
            <InputField
              type={InputfieldTypes.SELECT}
              name={AddTicketFormConstants.ORDER_SELECT_NAME}
              label={AddTicketFormConstants.ORDER_SELECT_LABEL}
              placeholder={SELECT_PLACEHOLDER}
              requiredField
              options={orderOptions}
              allowClear
              showSearch
              selectLoading={ordersFetching}
              optionFilterProp={OPTION_FILTER_PROP}
              changeHandler={(value) => {
                handleFieldChange(
                  AddTicketFormConstants.ORDER_SELECT_NAME,
                  value,
                );
              }}
            />
          </Col>
          <Col span={12}>
            <InputField
              type={InputfieldTypes.SELECT}
              name={AddTicketFormConstants.QUARRY_SELECT_NAME}
              label={AddTicketFormConstants.QUARRY_SELECT_LABEL}
              placeholder={SELECT_PLACEHOLDER}
              options={vendorOptions}
              allowClear
              showSearch
              selectLoading={vendorFetching}
              optionFilterProp={OPTION_FILTER_PROP}
              changeHandler={(value) => {
                handleFieldChange(
                  AddTicketFormConstants.QUARRY_SELECT_NAME,
                  value,
                );
              }}
            />
          </Col>
          <Col span={12}>
            <InputField
              type={InputfieldTypes.SELECT}
              name={AddTicketFormConstants.MATERIAL_SELECT_NAME}
              label={AddTicketFormConstants.MATERIAL_SELECT_LABEL}
              placeholder={SELECT_PLACEHOLDER}
              options={materialOptions}
              allowClear
              showSearch
              selectLoading={materialFetching}
              optionFilterProp={OPTION_FILTER_PROP}
              changeHandler={(value) => {
                handleFieldChange(
                  AddTicketFormConstants.MATERIAL_SELECT_NAME,
                  value,
                );
              }}
            />
          </Col>
          <Col span={12}>
            <InputField
              type={InputfieldTypes.SELECT}
              name={AddTicketFormConstants.TRUCK_SELECT_NAME}
              label={AddTicketFormConstants.TRUCK_SELECT_LABEL}
              placeholder={SELECT_PLACEHOLDER}
              options={truckNumbersOptions}
              requiredField
              allowClear
              showSearch
              selectLoading={trucksFetching}
              optionFilterProp={OPTION_FILTER_PROP}
            />
          </Col>
        </Row>
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <ConditionalRender visible={orderDetails}>
              <OrderDetailsSummary orderDetails={orderDetails} />
            </ConditionalRender>
          </Col>
          <Col span={24}>
            <AddTicketDragger
              onFilesUploaded={() => {}}
              docType={""}
              filesUploaded={[]}
              maxFileSize={1}
              allowedFileTypes={FileTypes.PDF}
              setDocumentKey={setDocumentKey}
              largeSizeFile={true}
            />
          </Col>
        </Row>
      </div>
    </>
  );
};

export default AddTicketForm;
